import { useEffect, useState, useCallback } from 'react'
import moment from 'moment'
import ReactMarkdown from 'react-markdown'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import LoadingSpinner from '@components/base/LoadingSpinner'
import {
  Event,
  EventAttendee,
  RegistrationField,
  TicketType,
  TicketOrder,
} from '@graphql/__generated/graphql'
import { Button } from '@components/inputs/Button'
import { TextInput } from '@components/inputs/TextInput'
import { SEND_ATTENDEE_EMAILS_FOR_EVENT } from '@graphql/events/sendAttendeeEmailsForEvent'
import { motion } from 'framer-motion'
import React from 'react'
import { convertAttendeesToCSV } from '../../libs/Utility/csv'
import Box from '../base/Box'
import AttendeeModal from './AttendeeModal'
import NotFoundModal from './NotFoundModal'
import QRScanner from '../base/QRScanner'
// import { debounce } from '@libs/Utility/util'
import { useTheme } from '@libs/useTheme'
// import useWindowSize from '@libs/useWindowSize'
import { GET_ORDERS_BY_EVENT_ID } from '@graphql/events/GetOrdersByEventId'
import { useAuth } from '@libs/Auth/useAuth'
import { GET_USERS_BY_IDS } from '@graphql/users/GetUsersByIds'
import { GET_ORDER_BY_ID } from '@graphql/events/GetOrderById'
import { GET_EVENT_BY_ID } from '@graphql/events/GetEventById'
import { GET_EVENT_CODES_BY_ID } from '@graphql/events/GetEventCodesById'
import { ATTENDEE_BOX_GET_USERS } from '@graphql/components/AttendeeBox.gql'
import { SET_CHECKIN_FOR_ATTENDEE } from '@graphql/events/setCheckInforAttendee'
import { SET_BADGE_PRINTED_FOR_ATTENDEE } from '@graphql/events/setBadgePrintedForAttendee'
import {
  IoArrowUndoOutline,
  IoCheckmarkCircleOutline,
  IoPrintOutline,
  IoRefreshOutline,
} from 'react-icons/io5'
import toast from 'react-hot-toast'
import {
  BadgePrints,
  createBadgeData,
  getBadgePrintedAt,
  printDymoBadge,
} from '@libs/dymoBadgePrinter'
import { getBadgePrintingConfig } from '@libs/badgePrintingConfig'

// type RefetchType = QueryResult['refetch']

type EventAttendeeWithRender = EventAttendee & {
  resentMessage?: string
}

export type AdditionalTicketInfo = {
  primaryTicketHolder?: string
  guestTicketHolder?: string
  guestTicketCode?: string
  orderId?: string
  isAdditionalGuest?: boolean
  discountCode?: string
}

const BADGE_PRINTING_EVENT_ID = 'e9b5be85-1f71-409b-8b4b-67d3700853f6'

const attendeeIdentity = (
  attendee: Pick<EventAttendee, 'ticketCode' | 'email' | 'orderId' | 'name'>
) =>
  attendee.ticketCode ??
  attendee.email ??
  `${attendee.orderId ?? 'attendee'}:${attendee.name ?? ''}`

const isEmailableAttendee = (
  attendee: Pick<EventAttendee, 'email' | 'isAdditionalGuest'>
): attendee is Pick<EventAttendee, 'email' | 'isAdditionalGuest'> & {
  email: string
} => Boolean(attendee.email?.trim() && !attendee.isAdditionalGuest)

export const AttendeeList = ({
  eventId,
  event,
  refetchEvent,
  totalIncludingHidden = 0,
  hiddenAttendees = 0,
}: {
  eventId: string
  event: Partial<Event>
  refetchEvent: () => Promise<unknown> | void
  totalIncludingHidden: number
  isAdmin?: boolean
  isOrganizer?: boolean
  hiddenAttendees?: number
}) => {
  // console.log({ event })
  const { isOrganizer, isAdmin, profile } = useAuth()

  const isPublished = event?.isPublished

  const registrationFields = (event?.registrationFields ?? []).filter(
    (f) => !!f && !!f.name
  )
  const registrationFieldNames = registrationFields?.map((f) => ({
    name: f.name,
  }))

  const attendees = event.attendees as EventAttendeeWithRender[]
  const invoiceTicketTitles = (event?.tickets ?? [])
    .filter((ticket) => ticket?.ticketType === TicketType.InvoiceTicket)
    .map((ticket) => ticket.title?.toUpperCase())
    .filter(Boolean)

  const canViewOrganizerInfo = isAdmin || isOrganizer
  const badgeConfig = getBadgePrintingConfig(eventId)
  const badgePrintingEnabled = Boolean(badgeConfig)

  const userIds: string[] = attendees
    .filter((a) => !!a.userId || !!a.email)
    .map((a) => a.userId ?? a.email)
    .filter((id): id is string => Boolean(id))

  const [userRegistrationFields, setUserRegistrationFields] = useState<
    { email: string; registrationFields: any }[]
  >([])

  // NOTE: Get reg field answers for attendee list entries
  const { loading: regFieldsLoading, refetch: refetchRegFields } = useQuery(
    // GET_USERS_BY_IDS,
    ATTENDEE_BOX_GET_USERS,
    {
      variables: {
        input: {
          // userIds,
          eventId: eventId,
        },
      },
      skip: (!isAdmin && !isOrganizer) || !event.event?.organizationName,
      onCompleted: (data) => {
        const currentAttendess = attendees
          .map((at) => at.email ?? at.userId)
          .filter((id): id is string => Boolean(id))

        const attendessMetadata =
          data.getRegistrationFieldAnswersByEventId.items
            .filter((item) => {
              const cond =
                ((!!item?.email && currentAttendess.includes(item.email)) ||
                  (!!item?.userId && currentAttendess.includes(item.userId))) &&
                // @ts-ignore
                Object.entries(item?.registrationFields ?? {}).length > 0

              return cond
            })
            .map((fd) => ({
              ...fd,
              registrationFields:
                fd.registrationFields?.[event!.event!.organizationName] ??
                fd.registrationFields,
            }))
        const registrationFields = currentAttendess.map((at) => {
          return {
            email: at,
            registrationFields:
              attendessMetadata.filter(
                (item) => {
                  return item?.email === at || item?.userId === at
                }

                // @ts-ignore
              )?.[0]?.registrationFields ?? {},
          }
        })
        setUserRegistrationFields(registrationFields)
      },
    }
  )

  // NOTE: Get order data for attendee list entries.
  const { data, loading: ordersLoading } = useQuery<{
    getOrdersByEventId: TicketOrder[] | null
  }>(GET_ORDERS_BY_EVENT_ID, {
    variables: {
      eventId,
      userIds: userIds,
    },
    skip: !eventId || userIds.length < 1 || !canViewOrganizerInfo,
    fetchPolicy: 'network-only',
  })

  const {
    data: eventData,
    loading: eventLoading,
    refetch: refetchBadgePrints,
  } = useQuery<{
    getEventById: Pick<Event, 'attendees' | 'discounts' | 'eventId'>
  }>(GET_EVENT_CODES_BY_ID, {
    variables: { input: { eventId } },
    skip:
      !eventId ||
      (!(
        isOrganizer &&
        event.event?.organizationName.toUpperCase() ===
          profile?.company?.toUpperCase()
      ) &&
        !isAdmin),
    fetchPolicy: 'network-only',
    pollInterval: canViewOrganizerInfo ? 10000 : 0,
  })

  // NOTE: Resend attendee invite email trigger.
  const [sendAttendeeEmailsForEvent] = useMutation(
    SEND_ATTENDEE_EMAILS_FOR_EVENT
  )
  const [setCheckInforAttendee] = useMutation(SET_CHECKIN_FOR_ATTENDEE)
  const [setBadgePrintedForAttendee] = useMutation(
    SET_BADGE_PRINTED_FOR_ATTENDEE
  )

  const [allAttendees, setAllAttendees] =
    useState<EventAttendeeWithRender[]>(attendees)
  const [filteredAttendees, setFilteredAttendees] =
    useState<EventAttendeeWithRender[]>(attendees)
  const [selectedAttendee, setSelectedAttendee] = useState<
    EventAttendeeWithRender | undefined
  >()

  const [additionalTickets, setAdditionalTickets] = useState<
    AdditionalTicketInfo[]
  >([])

  const [search, setSearch] = useState<string>('')
  const [nameSortAsc, setNameSortAsc] = useState<boolean>(true)
  const [emailSortAsc, setEmailSortAsc] = useState<boolean>(true)

  const [loading, setLoading] = useState<boolean>(false)
  const [checkInLoadingEmails, setCheckInLoadingEmails] = useState<string[]>([])
  const [badgePrintLoadingEmails, setBadgePrintLoadingEmails] = useState<
    string[]
  >([])
  const [badgePrints, setBadgePrints] = useState<BadgePrints>({})
  const [refreshing, setRefreshing] = useState<boolean>(false)

  const [showModal, setShowModal] = useState<boolean>(false)
  const [ticketCode, setTicketCode] = useState<string>('')
  const [pendingTicketCode, setPendingTicketCode] = useState<string>('')
  const [showNotFoundModal, setShowNotFoundModal] = useState<boolean>(false)
  const [openedQr, setOpenedQr] = useState<boolean>(false)

  const { theme } = useTheme()

  useEffect(() => {
    const sharedBadgePrints = (
      eventData?.getEventById.attendees ?? attendees
    ).reduce<BadgePrints>((prints, attendee) => {
      const identity = attendeeIdentity(attendee)
      const printedAt = attendee.badgePrintedAt
      if (printedAt) prints[identity.trim().toLowerCase()] = printedAt
      return prints
    }, {})

    setBadgePrints(sharedBadgePrints)
  }, [attendees, eventData])

  useEffect(() => {
    if (
      attendees.length > 0 &&
      !!data?.getOrdersByEventId &&
      data?.getOrdersByEventId?.length > 0
    ) {
      const payload: AdditionalTicketInfo[] = []
      data.getOrdersByEventId
        .filter((f) => !!f.paid)
        .forEach((order) => {
          const primaryTicketHolder = order.tickets![0]
          order.tickets?.forEach((ticket) => {
            payload.push({
              guestTicketHolder: ticket.email ?? undefined,
              guestTicketCode: ticket.ticketCode ?? undefined,
              primaryTicketHolder: primaryTicketHolder?.email ?? undefined,
              orderId: order.orderId,
              isAdditionalGuest: ticket.isAdditionalGuest ?? false,
              discountCode: ticket?.discountCode ?? undefined,
            })
          })
        })
      // console.log({ orderPayload: payload })
      setAdditionalTickets(payload)
    }
  }, [data, data?.getOrdersByEventId])

  const filterAttendees = (
    searchText: string,
    attendeesToFilter = allAttendees
  ) => {
    const lowercasedSearchText = searchText.toLowerCase()
    return attendeesToFilter.filter(
      (attendee) =>
        attendee?.name?.toLowerCase().includes(lowercasedSearchText) ||
        attendee.email?.toLowerCase().includes(lowercasedSearchText)
    )
  }

  useEffect(() => {
    const lowercasedSearchText = search.toLowerCase()
    const updatedFilteredAttendees = search
      ? attendees.filter(
          (attendee) =>
            attendee?.name?.toLowerCase().includes(lowercasedSearchText) ||
            attendee.email?.toLowerCase().includes(lowercasedSearchText)
        )
      : attendees

    setAllAttendees(attendees)
    setFilteredAttendees(updatedFilteredAttendees)
    setSelectedAttendee((currentSelectedAttendee) => {
      if (!currentSelectedAttendee) return currentSelectedAttendee
      return (
        attendees.find(
          (attendee) =>
            attendeeIdentity(attendee) ===
            attendeeIdentity(currentSelectedAttendee)
        ) ?? currentSelectedAttendee
      )
    })
    // Search already filters the current local attendees in handleSearch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendees])

  const ticketCodesLoaded = allAttendees.some(
    (attendee) => !!attendee.ticketCode
  )

  const handleTicketCode = useCallback(
    (text: string, deferUntilTicketCodesLoad = true) => {
      setTicketCode(text)
      setOpenedQr(true)
      const matchingAttendee = allAttendees.find(
        ({ ticketCode }) => ticketCode === text
      )
      if (matchingAttendee) {
        setPendingTicketCode('')
        handlePressAttendee(matchingAttendee)
      } else if (text && deferUntilTicketCodesLoad && !ticketCodesLoaded) {
        setPendingTicketCode(text)
      } else {
        setPendingTicketCode('')
        setShowNotFoundModal(true)
      }
    },
    [allAttendees, ticketCodesLoaded]
  )

  useEffect(() => {
    if (!pendingTicketCode || !ticketCodesLoaded) return
    handleTicketCode(pendingTicketCode, false)
  }, [handleTicketCode, pendingTicketCode, ticketCodesLoaded])

  useEffect(() => {
    if (openedQr) return
    if (allAttendees && allAttendees.length > 0) {
      const params = new URLSearchParams(window.location.search)
      const ticketCodeParam = params.get('ticketCode')
      if (ticketCodeParam) {
        handleTicketCode(ticketCodeParam)
      }
    }
  }, [openedQr, allAttendees, handleTicketCode])

  const handleOnQrRead = (text: string) => {
    try {
      const url = new URL(text)
      const params = new URLSearchParams(url.search)
      const code = params.get('ticketCode')
      if (code) {
        if (!text.includes(eventId)) {
          window.location.href = text
          return
        }
        handleTicketCode(code)
      } else {
        handleTicketCode('', false)
      }
    } catch (error) {
      handleTicketCode('', false)
      console.error('Invalid URL:', error)
      return null
    }
  }

  const handleSearch = (text: string) => {
    setSearch(text)
    setFilteredAttendees(filterAttendees(text))
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await Promise.all([
        refetchEvent(),
        refetchRegFields(),
        refetchBadgePrints(),
      ])
    } catch (error) {
      console.error('Failed to refresh attendees:', error)
      toast(`Failed to refresh attendee list.`, {
        icon: '❌',
        duration: 8000,
        className: 'custom-toast',
      })
    } finally {
      setRefreshing(false)
    }
  }

  const sortAttendees = (
    attendees: EventAttendeeWithRender[],
    field: keyof EventAttendeeWithRender,
    asc: boolean
  ) => {
    return attendees.sort((a, b) => {
      if (asc) {
        return String(a[field]).localeCompare(String(b[field]))
      }
      return String(b[field]).localeCompare(String(a[field]))
    })
  }

  const handleSortName = () => {
    setNameSortAsc(!nameSortAsc)
    setFilteredAttendees(
      sortAttendees([...filteredAttendees], 'name', !nameSortAsc)
    )
  }

  const handleSortEmail = () => {
    setEmailSortAsc(!emailSortAsc)
    setFilteredAttendees(
      sortAttendees([...filteredAttendees], 'email', !emailSortAsc)
    )
  }

  const sendInvitations = async (attendees: string[]) => {
    setLoading(true)
    await sendAttendeeEmailsForEvent({
      variables: {
        emails: attendees,
        eventId: eventId,
      },
    })
    const updatedAttendees = [...allAttendees].map((attendee) =>
      attendee.email && attendees.includes(attendee.email)
        ? { ...attendee, resentMessage: 'Email Resent' }
        : attendee
    )
    setAllAttendees(updatedAttendees)
    setFilteredAttendees(updatedAttendees)
    setSearch('')
    setLoading(false)
  }

  const handleResend = (attendee: EventAttendeeWithRender) => {
    if (isEmailableAttendee(attendee)) sendInvitations([attendee.email])
  }

  const updateLocalCheckInStatus = (
    identity: string,
    checkInStatus: string,
    checkInDatetime?: string
  ) => {
    const updateAttendee = (attendee: EventAttendeeWithRender) =>
      attendeeIdentity(attendee) === identity
        ? {
            ...attendee,
            checkInStatus,
            checkInDatetime,
          }
        : attendee

    setAllAttendees((currentAttendees) => currentAttendees.map(updateAttendee))
    setFilteredAttendees((currentAttendees) =>
      currentAttendees.map(updateAttendee)
    )
    setSelectedAttendee((currentAttendee) =>
      currentAttendee ? updateAttendee(currentAttendee) : currentAttendee
    )
  }

  const handleCheckInChange = async (
    attendee: EventAttendeeWithRender,
    checkInStatus: string
  ) => {
    const identity = attendeeIdentity(attendee)
    setCheckInLoadingEmails((currentEmails) => [...currentEmails, identity])
    try {
      await setCheckInforAttendee({
        variables: {
          email: attendee.ticketCode ? undefined : attendee.email,
          ticketCode: attendee.ticketCode,
          eventId,
          checkInStatus,
        },
      })
      updateLocalCheckInStatus(
        identity,
        checkInStatus,
        checkInStatus === 'present' ? new Date().toISOString() : undefined
      )
    } catch (error) {
      console.error('Failed to update attendee check-in status:', error)
      toast(`Failed to update check-in status.`, {
        icon: '❌',
        duration: 8000,
        className: 'custom-toast',
      })
    } finally {
      setCheckInLoadingEmails((currentEmails) =>
        currentEmails.filter((email) => email !== identity)
      )
    }
  }

  const handlePrintBadge = async (attendee: EventAttendeeWithRender) => {
    if (!badgeConfig) return
    const identity = attendeeIdentity(attendee)
    setBadgePrintLoadingEmails((currentEmails) => [...currentEmails, identity])
    try {
      const registrationFields = userRegistrationFields.find(
        (entry) =>
          !!attendee.email &&
          entry.email?.toLowerCase() === attendee.email.toLowerCase()
      )?.registrationFields
      const badge = createBadgeData(
        attendee,
        registrationFields,
        badgeConfig.details
      )
      const printerName = await printDymoBadge(badge, badgeConfig.debug)
      if (!printerName) {
        toast.success('Badge content logged to console.')
        return
      }
      const printedAt = new Date().toISOString()
      setBadgePrints((currentPrints) => ({
        ...currentPrints,
        [identity.trim().toLowerCase()]: printedAt,
      }))

      let statusSaved = false
      try {
        statusSaved = Boolean(
          await setBadgePrintedForAttendee({
            variables: {
              email: attendee.ticketCode ? undefined : attendee.email,
              ticketCode: attendee.ticketCode,
              eventId,
            },
          }).then((result) => result.data?.setBadgePrintedForAttendee)
        )
      } catch (error) {
        console.error('Failed to share badge printed status:', error)
      }

      if (!statusSaved) {
        toast.error(
          `Badge sent to ${printerName}, but its printed status could not be shared with other staff.`
        )
        return
      }

      toast.success(`Badge sent to ${printerName}.`)
    } catch (error) {
      console.error('Failed to print attendee badge:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to print badge.'
      )
    } finally {
      setBadgePrintLoadingEmails((currentEmails) =>
        currentEmails.filter((currentIdentity) => currentIdentity !== identity)
      )
    }
  }

  const handleResendAll = () => {
    const pendingAttendess = allAttendees
      .filter(
        (attendee) =>
          !attendee.resentMessage &&
          !attendee.registered &&
          isEmailableAttendee(attendee)
      )
      .map((attendee) => attendee.email)
      .filter((email): email is string => Boolean(email))
    sendInvitations(pendingAttendess)
  }

  const formatDate = (date: string) => {
    if (!date) return ''
    return moment(date).format('MMM DD, H:mmA')
  }

  const getStatusMessage = (attendee: EventAttendeeWithRender) => {
    if (attendee?.resentMessage) {
      return attendee.resentMessage
    }
    if (isPublished) {
      if (attendee.registered) {
        return 'Registered'
      }
      const dateMessage = attendee?.invitationSentDatetime
        ? formatDate(attendee?.invitationSentDatetime)
        : null
      if (dateMessage) {
        return (
          <>
            Sent <br />
            {dateMessage}
          </>
        )
      }
      return 'Sent'
    }
    return 'Not sent yet'
  }

  const handlePressAttendee = (attendee: EventAttendeeWithRender) => {
    setSelectedAttendee(attendee)
    setShowModal(true)
  }

  const pendingAttendees = allAttendees.filter(
    (attendee) => !attendee.registered && isEmailableAttendee(attendee)
  )

  if (!attendees || attendees.length === 0) {
    return (
      <Box className="px-8">
        <ReactMarkdown>
          No attendees have been added for this event yet. Check back soon!
        </ReactMarkdown>
      </Box>
    )
  }

  const numberOfAttendees = totalIncludingHidden || (attendees?.length ?? 0)
  const markedAsPresent = allAttendees.filter(
    ({ checkInStatus }) => checkInStatus == 'present'
  ).length
  const notCheckedIn = allAttendees.filter(
    ({ checkInStatus }) => checkInStatus !== 'present'
  ).length

  // const [fetchOrder] = useLazyQuery(GET_ORDER_BY_ID, {
  //   fetchPolicy: 'network-only',
  // })

  const getOrder = async (attendee: EventAttendee) => {
    if (!attendee.orderId) return null
    // const order = await fetchOrder({
    //   variables: { orderId: attendee.orderId },
    // }).then((d) => ({ ...d.data?.getOrderById, orderId: attendee.orderId! }))
    if (
      !loading &&
      !!data?.getOrdersByEventId &&
      data.getOrdersByEventId.length > 0
    ) {
      const order = data.getOrdersByEventId.find(
        (o) => o.orderId === attendee.orderId || o.userId === attendee.userId
      )

      // console.log({ order })
      return order
    }
    return null
  }

  const downloadCSV = async (
    targetAttendees = allAttendees,
    filename = 'attendees.csv'
  ) => {
    // const attendeesWithOrders: EventAttandeeWithOrder[] = await Promise.all(
    //   attendees.map(async (at, index) => {
    //     const order = await getOrder(at)
    //
    //     if (order) return { ...at, order }
    //     return { ...at }
    //   })
    // )

    // console.log({ eventData, additionalTickets })

    const csv = convertAttendeesToCSV(
      targetAttendees,
      registrationFieldNames,
      userRegistrationFields,
      additionalTickets,
      event?.tickets ?? [],
      eventData?.getEventById?.discounts ?? []
    )
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadInvoiceCSV = () => {
    const invoiceAttendees = allAttendees.filter((attendee) =>
      invoiceTicketTitles.includes(attendee.ticketTitle?.toUpperCase())
    )
    downloadCSV(invoiceAttendees, 'invoice-ticket-attendees.csv')
  }

  const getCheckInStatusLabel = (checkInStatus?: string | null) => {
    if (checkInStatus === 'present') return 'Present'
    return 'No activity'
  }

  const renderCheckInControls = (attendee: EventAttendeeWithRender) => {
    const identity = attendeeIdentity(attendee)
    const isLoading = checkInLoadingEmails.includes(identity)
    const isPrinting = badgePrintLoadingEmails.includes(identity)
    const isPresent = attendee.checkInStatus === 'present'
    const badgePrintedAt = getBadgePrintedAt(badgePrints, identity)

    return (
      <Box className="flex flex-col gap-2">
        <span className={`capitalize ${isPresent ? 'text-green-500' : ''}`}>
          {getCheckInStatusLabel(attendee.checkInStatus)}
        </span>
        <Box className="flex flex-wrap gap-2">
          {!isPresent && (
            <Button
              title="Check In"
              icon={<IoCheckmarkCircleOutline size="1em" />}
              type="success"
              size="small"
              loading={isLoading}
              disabled={isLoading}
              onClick={() => handleCheckInChange(attendee, 'present')}
              className="whitespace-nowrap"
            />
          )}
          {isPresent && (
            <>
              <Button
                title="Undo"
                icon={<IoArrowUndoOutline size="1em" />}
                size="small"
                loading={isLoading}
                disabled={isLoading || isPrinting}
                onClick={() => handleCheckInChange(attendee, '')}
                className="whitespace-nowrap"
              />
              {badgePrintingEnabled && (
                <Button
                  title={badgePrintedAt ? 'Print Again' : 'Print Badge'}
                  icon={<IoPrintOutline size="1em" />}
                  type={badgePrintedAt ? 'danger' : 'primary'}
                  backgroundColor={badgePrintedAt ? '#dc2626' : undefined}
                  textColor={badgePrintedAt ? '#ffffff' : undefined}
                  size="small"
                  loading={isPrinting}
                  disabled={isLoading || isPrinting || regFieldsLoading}
                  onClick={() => handlePrintBadge(attendee)}
                  className="whitespace-nowrap"
                />
              )}
            </>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box className="flex items-center gap-2">
        <TextInput
          value={search}
          className="w-full"
          label=""
          placeholder="Enter your search terms"
          onChange={handleSearch}
        />
        {canViewOrganizerInfo && (
          <Button
            title="Refresh"
            icon={<IoRefreshOutline size="1em" />}
            loading={refreshing}
            disabled={refreshing}
            onClick={handleRefresh}
            size="large"
            className="whitespace-nowrap"
          />
        )}
        <QRScanner onRead={handleOnQrRead} />
      </Box>
      {pendingAttendees.length > 0 && (
        <Box className="mt-3 flex items-center space-x-2">
          <a
            className="text-link hover:underline cursor-pointer"
            onClick={handleResendAll}
          >
            Resend pending invites
          </a>
          {loading && <LoadingSpinner className="mt-1" size="small" />}
        </Box>
      )}
      <Box className="w-full pt-5 text-xs sm:text-sm ">
        <Box
          color="backgroundColourSecondary"
          className="grid grid-cols-3 my-3"
        >
          <Box
            color="backgroundColourSecondary"
            className="p-3 border-l-8 border-l-blue-500 text-left"
          >
            <span className="">Total Registered: </span>
            <span>{numberOfAttendees}</span>
          </Box>
          {canViewOrganizerInfo && (
            <Box className="p-3 text-left">
              <span className="">Marked as Present: </span>
              <span className="text-green-500">{markedAsPresent}</span>
            </Box>
          )}
          {canViewOrganizerInfo && (
            <Box className="p-3 border-r-8 text-left">
              <span className="">Not Checked In: </span>
              <span>{notCheckedIn}</span>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        color="backgroundColourSecondary"
        className={`
          grid col-span-4 grid-cols-4 border-collapse text-[9px] sm:text-sm
          ${
            canViewOrganizerInfo
              ? 'md:col-span-5 md:grid-cols-5'
              : 'md:col-span-1 md:grid-cols-1'
          }
        `}
      >
        <Box
          className="p-2 md:p-7 border border-forumm-menu-border cursor-pointer"
          onClick={handleSortName}
        >
          Full Name {nameSortAsc ? '↓' : '↑'}
        </Box>
        {canViewOrganizerInfo && (
          <Box
            className="p-2 md:p-7 border border-forumm-menu-border cursor-pointer"
            onClick={handleSortEmail}
          >
            Email {emailSortAsc ? '↓' : '↑'}
          </Box>
        )}
        {canViewOrganizerInfo && (
          <Box className="p-2 md:p-7 border border-forumm-menu-border">
            Check-in Status
          </Box>
        )}
        {canViewOrganizerInfo && (
          <Box className="p-2 md:p-7 border border-forumm-menu-border hidden md:block">
            Reminder
          </Box>
        )}
        {canViewOrganizerInfo && (
          <Box className="p-2 md:p-7 border border-forumm-menu-border">
            View More Info
          </Box>
        )}
        {filteredAttendees.map((attendee, i) => (
          <motion.div
            className={`grid col-span-4 grid-cols-4 border-forumm-menu-border border-2 -mt-1
                        ${
                          canViewOrganizerInfo
                            ? 'md:col-span-5 md:grid-cols-5'
                            : 'md:col-span-1 md:grid-cols-1'
                        }
              `}
            style={{ background: theme.backgroundColourSecondary }}
            whileHover={{ scale: 1.02 }}
            key={attendeeIdentity(attendee)}
          >
            <Box
              className="p-2 md:p-7 mt-2 cursor-pointer text-[9px] sm:text-sm"
              onClick={() => handlePressAttendee(attendee)}
            >
              <span>{attendee?.name}</span>
              {badgePrintingEnabled &&
                getBadgePrintedAt(badgePrints, attendeeIdentity(attendee)) && (
                  <span
                    className="mt-1 flex items-center gap-1 text-[8px] font-semibold text-green-600 sm:text-xs"
                    title={`Printed ${moment(
                      getBadgePrintedAt(badgePrints, attendeeIdentity(attendee))
                    ).format('DD MMM YYYY, HH:mm')}`}
                  >
                    <IoPrintOutline aria-hidden="true" />
                    Badge printed
                  </span>
                )}
            </Box>
            {canViewOrganizerInfo && (
              <Box
                className="p-2 md:p-7 text-[9px] sm:text-sm mt-2 cursor-pointer overflow-auto word-wrap"
                onClick={() => handlePressAttendee(attendee)}
              >
                {attendee.isAdditionalGuest
                  ? 'Managed by registrar'
                  : (attendee.email ?? '—')}
              </Box>
            )}
            {canViewOrganizerInfo && (
              <Box className="p-2 md:p-7 text-[9px] sm:text-sm mt-2">
                {renderCheckInControls(attendee)}
              </Box>
            )}
            {canViewOrganizerInfo && (
              <Box className="p-2 md:p-7 text-[9px] sm:text-sm mt-2 hidden md:block">
                {attendee.isAdditionalGuest ? (
                  'Managed by registrar'
                ) : (
                  <>
                    {!attendee.registered &&
                      !attendee.resentMessage &&
                      isEmailableAttendee(attendee) && (
                        <Button
                          title="Resend"
                          type="modal"
                          size="small"
                          onClick={() => handleResend(attendee)}
                        />
                      )}
                    {attendee.registered && 'Registered'}
                    {!attendee.registered &&
                      attendee.resentMessage &&
                      'Invitation Resent!'}
                  </>
                )}
              </Box>
            )}
            {canViewOrganizerInfo && (
              <Box
                className="p-2 md:p-7 mt-2 cursor-pointer"
                onClick={() => handlePressAttendee(attendee)}
              >
                <Button
                  title="i"
                  size="auto"
                  className={`text-white text-[9px] sm:text-sm p-0`}
                  onClick={() => handlePressAttendee(attendee)}
                />
              </Box>
            )}
          </motion.div>
        ))}
      </Box>
      {canViewOrganizerInfo && (
        <Box className="w-full flex items-center justify-between pt-8">
          <Box className="flex text-center pl-3 md:pl-0">
            <Button
              title="Export CSV"
              size="auto"
              // TODO: this should be visible, review it after.
              // icon={<IoDownloadOutline size="1.5em" />}
              onClick={() => downloadCSV()}
              className="ml-2 text-white whitespace-nowrap"
              loading={regFieldsLoading || ordersLoading || eventLoading}
            />
            {invoiceTicketTitles.length > 0 && (
              <Button
                title="Export Invoice CSV"
                size="auto"
                onClick={downloadInvoiceCSV}
                className="ml-2 text-white whitespace-nowrap"
                loading={regFieldsLoading || ordersLoading || eventLoading}
              />
            )}
          </Box>
          <QRScanner onRead={handleOnQrRead} />
        </Box>
      )}
      <AttendeeModal
        key={selectedAttendee ? attendeeIdentity(selectedAttendee) : undefined}
        eventId={eventId}
        show={showModal}
        setShow={setShowModal}
        attendee={selectedAttendee}
        attendees={allAttendees}
        regFieldsLoading={regFieldsLoading}
        registrationFields={registrationFields}
        userRegistrationFields={userRegistrationFields}
        onCheckInChange={handleCheckInChange}
        onPrintBadge={handlePrintBadge}
        badgePrints={badgePrints}
        refetchRegFields={refetchRegFields}
      />
      <NotFoundModal
        ticketCode={ticketCode}
        show={showNotFoundModal}
        setShow={setShowNotFoundModal}
      />
    </>
  )
}
